import { useEffect, useState } from 'react';
import baseapi from '../../baseapi';

const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-IN'));
const _COUNTING = ['online', 'replay'];

/**
 * The summary row of the Queue Operations page, from camera counts only:
 * GET /api/overview (people in holding, in the queue zones, darshan completed) and GET /api/holding
 * (entered / released through the holding areas' entry and exit cameras). A figure that no camera measures
 * shows "—" with the reason, never an estimate.
 */
export function QueueSummaryBar() {
  const [ov, setOv] = useState<any | null>(null);
  const [hold, setHold] = useState<any | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      baseapi.get('overview').then(r => { if (alive && r.data?.kpis) setOv(r.data); }).catch(() => { /* keep the last figures */ });
      baseapi.get('holding').then(r => { if (alive && Array.isArray(r.data?.areas)) setHold(r.data); }).catch(() => { /* keep the last figures */ });
    };
    load();
    const t = window.setInterval(load, 5000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  const k = ov?.kpis ?? {};
  const areas: any[] = hold?.areas ?? [];
  const sum = (key: string) => {
    const v = areas.map(a => a[key]).filter((x: any) => typeof x === 'number');
    return v.length ? v.reduce((a: number, b: number) => a + b, 0) : null;
  };
  const exitCams = areas.flatMap(a => a.exit_cameras ?? []);
  const entryCams = areas.flatMap(a => a.entry_cameras ?? []);
  const exitCounting = exitCams.some((c: any) => _COUNTING.includes(c.state));
  const entryCounting = entryCams.some((c: any) => _COUNTING.includes(c.state));
  const released15 = sum('released_15m');
  const s = hold?.summary;

  const tile = (label: string, value: string, sub: string, tone = 'text-neutral-900 dark:text-neutral-100', unit?: string) => (
    <div className="min-w-0">
      <div className="text-xs sm:text-[12.5px] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1 font-semibold truncate">{label}</div>
      <div className={`font-mono text-2xl sm:text-3xl font-bold leading-tight ${tone}`}>
        {value}{unit && value !== '—' && <span className="text-sm font-normal text-neutral-400"> {unit}</span>}
      </div>
      <div className="text-[11px] sm:text-xs text-neutral-400 dark:text-neutral-500 font-mono mt-1 truncate" title={sub}>{sub}</div>
    </div>
  );

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5 md:gap-4 items-center bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl px-4 py-3.5 sm:px-5 sm:py-4 shadow-xs">
      {tile('In holding areas', fmt(k.holding),
        s ? `people in the boxes now · ${s.boxes_counted ?? 0} of ${s.boxes_with_camera ?? 0} box cameras counting` : 'people in the boxes now')}
      {tile('Entered holding today', entryCounting || sum('entered_today') ? fmt(sum('entered_today')) : '—',
        !entryCams.length ? 'no entry camera yet' : entryCounting ? `+${fmt(sum('entered_15m'))} in the last 15 min` : 'entry camera not counting now')}
      {tile('Releasing rate', exitCounting && released15 != null ? fmt(Math.round(released15 / 15)) : '—',
        !exitCams.length ? 'no exit camera yet' : exitCounting ? `released today ${fmt(sum('released_today'))}` : 'holding exit camera not counting now',
        'text-blue-600 dark:text-blue-400', '/min')}
      {tile('Darshan completed', fmt(k.darshan), 'counted at the temple exits today', 'text-amber-600 dark:text-amber-400')}
      <div className="col-span-2 md:col-span-1">
        {tile('In queue zones', fmt(k.inQueue), 'people in all queue zones now', 'text-emerald-600 dark:text-emerald-400')}
      </div>
    </div>
  );
}
